import React, { useContext, useEffect, useRef, useState } from 'react';
import { MandatoryChildren } from '../../../../models/children-props';
import { PostingPlanContext } from '../../../../providers/PostingPlanProvider';
import { useDragDropManager, useDrop, XYCoord } from 'react-dnd';
import { CalendarDataType, PostingPlanCellDataType, TimeslotDragType } from '../../../../models/calendar';
import { TimeslotMappedData } from '../PostingPlan.view';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';
import { Unsubscribe } from 'dnd-core/dist/interfaces';
import classNames from 'classnames';
import classes from './DroppableTableCell.module.scss';
import { useApolloClient } from '@apollo/client';
import {
  StartOfWeekFormatSetting,
  TimeslotDay,
  TimeslotInput,
  UpdateTimeslotMutation,
  useUpdateTimeslotMutation
} from '../../../../models/graphql';
import { triggerErrorToast } from '../../../../helpers/triggerErrorToast';
import { AppContext } from '../../../../providers/AppProvider';
import { CalendarCellActionLabel } from '../../../../components/actions/CalendarCellActionLabel';
import { getApolloErrorMessage } from 'helpers/remapApolloErrors';

export type DroppableTableCellViewProps = MandatoryChildren<{
  position: [number, number];
  dayStartOfWeek: StartOfWeekFormatSetting;
}>;

export const DroppableTableCellView = (props: DroppableTableCellViewProps): JSX.Element => {
  const { setTimeslots, timeslots, setSource, source, cellClickHandler, refetchTimeslots } =
    useContext(PostingPlanContext);
  const { selectedWorkspaceId, setLimitReachedModalState } = useContext(AppContext);
  const minute = useRef(0);
  const dragDropManager = useDragDropManager();
  const monitor = dragDropManager.getMonitor();
  const { cache: apolloCache } = useApolloClient();
  const { position, children } = props;
  const [hour, day] = position;
  const [updateTimeslot] = useUpdateTimeslotMutation();
  const [shouldUpdate, setShouldUpdate] = useState<TimeslotDragType | null>(null);

  const setNewState = (item: TimeslotDragType) => {
    const newState = [...timeslots] as CalendarDataType<PostingPlanCellDataType>;
    const tsObjectArray = newState[item.startPosition[0]][item.startPosition[1]] as TimeslotMappedData[]; //find old object
    const timeslotIndex = tsObjectArray.findIndex(timeslot => timeslot.id === item.id); // old item index
    const newToAdd = tsObjectArray[timeslotIndex];
    const hours = hour < 10 ? `${hour}`.padStart(2, '0') : `${hour}`;
    const minutes = minute.current < 10 ? `${minute.current}`.padStart(2, '0') : `${minute.current}`;
    newToAdd.day = PostingPlanHelpers.getDayFromColumn(day, props.dayStartOfWeek);
    newToAdd.time = `${hours}:${minutes}`;

    //check if mergable
    const sameTimeAndCategoryItemIndex = newState[hour][day]?.findIndex(
      timeslot =>
        timeslot.categoryId === item.categoryId &&
        timeslot.time === newToAdd.time &&
        timeslot.day === newToAdd.day &&
        timeslot.id !== item.id
    );
    if (sameTimeAndCategoryItemIndex !== undefined && sameTimeAndCategoryItemIndex > -1) {
      const sameTimeAndCategoryItem = (newState[hour][day] as TimeslotMappedData[])[sameTimeAndCategoryItemIndex];
      const profilesToPush = sameTimeAndCategoryItem.socialProfiles.filter(
        profile => !item.socialProfiles.find(existingProfiles => existingProfiles.id === profile.id)
      );
      if (profilesToPush.length) {
        item.socialProfiles.push(...profilesToPush);
        newToAdd.socialProfiles = item.socialProfiles.sort((a, b) => +a.id - +b.id);
      }
      //remove the old one and evict from cache
      const cacheId = apolloCache.identify({ id: sameTimeAndCategoryItem.id, __typename: 'Timeslot' });
      if (cacheId) {
        apolloCache.evict({ id: cacheId });
        apolloCache.gc();
      }
      (newState[hour][day] as TimeslotMappedData[]).splice(sameTimeAndCategoryItemIndex, 1);
    }

    //set new and remove old
    tsObjectArray.splice(timeslotIndex, 1);
    (newState[item.startPosition[0]][item.startPosition[1]] as TimeslotMappedData[]) = tsObjectArray;

    (newState[hour][day] as TimeslotMappedData[]) = PostingPlanHelpers.sortByTime([
      ...(newState[hour][day] as TimeslotMappedData[]),
      newToAdd
    ]);

    setSource(null);
    setTimeslots(newState);
  };

  const drop = (item: TimeslotDragType) => {
    // this prevents flickering effect upon drop.
    // we first set setNewState in order visually get the dropped state ASAP, and in parallel we call the update mutation optimistically. If all is ok no issues, if it fails, it will refresh the page with proper data
    setNewState(item);
    setShouldUpdate(item);
  };

  useEffect(() => {
    if (shouldUpdate) {
      const hours = hour < 10 ? `${hour}`.padStart(2, '0') : `${hour}`;
      // Rapid drag-n-drop can result is both negative numbers and number greater than 60
      const safeMinute = minute.current < 0 ? 0 : minute.current > 55 ? 55 : minute.current;
      const minutes = safeMinute < 10 ? `${safeMinute}`.padStart(2, '0') : `${safeMinute}`;
      const input: TimeslotInput = {
        day: PostingPlanHelpers.getDayFromColumn(day, props.dayStartOfWeek),
        time: `${hours}:${minutes}`
      };
      const optimisticResponse: UpdateTimeslotMutation['updateTimeslot'] = {
        __typename: 'Timeslot',
        id: shouldUpdate.id,
        day: input.day as TimeslotDay,
        time: input.time as string,
        onboardingContent: false,
        category: {
          __typename: 'Category',
          id: shouldUpdate.categoryId
        },
        socialProfiles: shouldUpdate.socialProfiles.map(profile => ({
          id: profile.id,
          __typename: profile.__typename
        }))
      };
      updateTimeslot({
        variables: {
          timeslotId: shouldUpdate.id,
          input: input
        },
        optimisticResponse: {
          updateTimeslot: optimisticResponse
        }
      }).catch(e => {
        if (selectedWorkspaceId) {
          refetchTimeslots?.({ workspaceId: selectedWorkspaceId });
        }
        if (getApolloErrorMessage(e) === 'PLAN_LIMIT_EXCEEDED') {
          setLimitReachedModalState({
            isOpen: true,
            type: 'timeslot'
          });
          return;
        }
        triggerErrorToast('Error in updating the timeslot')(e);

        // we use refetch in order to force render, since it might happen that our optimistic update (setTimeslots) left our item in new location, and we want to prevent this. refetch will reload the page with proper state
      });
      setShouldUpdate(null);
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [shouldUpdate]);

  const [{ isOver, canDrop, handlerId }, dropRef] = useDrop(
    () => ({
      accept: DraggableTypesObject.TIMESLOT,
      drop,
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        handlerId: monitor.getHandlerId() as string | null
      })
    }),
    [props.position]
  );

  useEffect(() => {
    let unsubscribe: Unsubscribe | undefined;
    if (isOver) {
      unsubscribe = monitor.subscribeToOffsetChange(() => {
        let target;
        const targetIds = monitor.getTargetIds();
        const targetId = targetIds.length ? (targetIds[0] as string) : '';
        const targetReg = targetId ? dragDropManager.getRegistry().getTarget(targetId) : null;
        const targetEl = document.getElementById(targetId);
        if (targetReg && targetEl) {
          const dayStr = targetEl.getAttribute('data-day') as string;
          const day = +dayStr;
          const hourStr = targetEl.getAttribute('data-hour') as string;
          const hour = +hourStr;
          const { height, y } = targetEl.getBoundingClientRect();
          target = { day, hour, height, y };
        }
        if (!target) {
          return;
        }
        const offset = monitor.getClientOffset();
        if (target && offset) {
          const { y } = offset as XYCoord;
          const dy = y - target.y;
          const snap = Math.round(target.height / 12);
          minute.current = Math.floor(dy / snap) * 5;
        }
      });
    }
    return unsubscribe;
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [monitor, isOver]);

  const hovered = isOver && canDrop ? true : null;
  const globalClasses = classNames(classes.DroppableTableCell, {
    [classes.isSource]: JSON.stringify(source) === JSON.stringify(position) || isOver,
    [classes.Dragging]: isOver
  });

  return (
    <td
      className={globalClasses}
      data-hovered={hovered}
      data-hour={hour}
      data-day={day}
      ref={dropRef}
      id={handlerId || `${hour}-${day}-table-cell`}
      key={`${day}-${hour}-table-cell`}>
      <CalendarCellActionLabel labelText={'Add Timeslot'} clickHandler={() => cellClickHandler(position)} />
      {children}
    </td>
  );
};
